'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { io, type Socket } from 'socket.io-client';

import {
    REALTIME_CHANNELS,
    REALTIME_NAMESPACE,
    REALTIME_SOCKET_EVENTS,
    type RealtimeChannel,
    type RealtimeEnvelope,
    USER_PROFILE_REALTIME_TOPICS,
    type UserProfileInvalidatedPayload,
} from '@repo/contracts/realtime';

import { useAppContext } from '@context/AppContext';

import { getPublicApiUrl } from '@utils/runtime-config';

type RealtimeConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

type RealtimeSubscription<TPayload = unknown> = {
    channel: RealtimeChannel;
    topic: string;
    onMessage: (event: RealtimeEnvelope<TPayload>) => void;
    filter?: (event: RealtimeEnvelope<TPayload>) => boolean;
};

type InternalRealtimeSubscription = {
    channel: RealtimeChannel;
    topic: string;
    onMessage: (event: RealtimeEnvelope) => void;
    filter?: (event: RealtimeEnvelope) => boolean;
};

type RealtimeContextValue = {
    status: RealtimeConnectionStatus;
    error: Error | null;
    connected: boolean;
    ensureConnected: () => Promise<Socket>;
    disconnect: () => void;
    subscribe: <TPayload>(subscription: RealtimeSubscription<TPayload>) => () => void;
};

type RealtimeProviderProps = {
    children: React.ReactNode;
};

type RealtimeSocketConfig = {
    managerUrl: string;
    namespace: string;
    path: string;
};

type UseRealtimeChannelOptions<TPayload> = {
    channel: RealtimeChannel;
    topic: string;
    enabled?: boolean;
    onMessage: (event: RealtimeEnvelope<TPayload>) => void;
    filter?: (event: RealtimeEnvelope<TPayload>) => boolean;
};

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

let unauthorizedEventEmitted = false;
let unauthorizedTimeout: ReturnType<typeof setTimeout> | null = null;

const persistNewToken = async (token: string): Promise<void> => {
    await fetch('/api/auth/update-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
        credentials: 'include',
    });
};

const getRealtimeSocketConfig = (): RealtimeSocketConfig => {
    const rawApiUrl = getPublicApiUrl().trim();
    if (!rawApiUrl) {
        throw new Error('API_URL is not configured. Please set NEXT_PUBLIC_API_URL environment variable.');
    }

    let apiUrl: URL;
    try {
        apiUrl = new URL(rawApiUrl);
    } catch {
        throw new Error(`NEXT_PUBLIC_API_URL must be an absolute URL. Received "${rawApiUrl}".`);
    }

    const apiBasePath = apiUrl.pathname.replace(/\/$/, '');

    return {
        managerUrl: apiUrl.origin,
        namespace: REALTIME_NAMESPACE,
        path: `${apiBasePath || ''}/socket.io`,
    };
};

const dispatchUnauthorizedEventOnce = () => {
    if (typeof window === 'undefined' || unauthorizedEventEmitted) {
        return;
    }

    unauthorizedEventEmitted = true;
    window.dispatchEvent(new CustomEvent('UNAUTHORIZED'));

    if (unauthorizedTimeout) {
        clearTimeout(unauthorizedTimeout);
    }

    unauthorizedTimeout = setTimeout(() => {
        unauthorizedEventEmitted = false;
        unauthorizedTimeout = null;
    }, 3000);
};

const isUserProfileInvalidatedEvent = (event: RealtimeEnvelope): event is RealtimeEnvelope<UserProfileInvalidatedPayload> => (
    event.channel === REALTIME_CHANNELS.userProfile && event.topic === USER_PROFILE_REALTIME_TOPICS.invalidated
);

export function RealtimeProvider({ children }: RealtimeProviderProps): React.JSX.Element {
    const [user, setUser] = useAppContext('user');
    const socketRef = useRef<Socket | null>(null);
    const subscribersRef = useRef(new Map<string, InternalRealtimeSubscription>());
    const subscriptionIdRef = useRef(0);
    const disconnectRequestedRef = useRef(false);
    const setUserRef = useRef(setUser);

    const [status, setStatus] = useState<RealtimeConnectionStatus>('disconnected');
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        setUserRef.current = setUser;
    }, [setUser]);

    const handleRealtimeMessage = useCallback((event: RealtimeEnvelope) => {
        if (isUserProfileInvalidatedEvent(event)) {
            setUserRef.current({
                group: Array.isArray(event.payload.group) ? event.payload.group : [],
            });
        }

        subscribersRef.current.forEach((subscription) => {
            if (subscription.channel !== event.channel || subscription.topic !== event.topic) {
                return;
            }

            if (subscription.filter && !subscription.filter(event)) {
                return;
            }

            subscription.onMessage(event);
        });
    }, []);

    const teardownSocket = useCallback(() => {
        if (!socketRef.current) {
            return;
        }

        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
    }, []);

    const initializeSocket = useCallback((): Socket => {
        if (socketRef.current) {
            return socketRef.current;
        }

        const { managerUrl, namespace, path } = getRealtimeSocketConfig();
        const socket = io(`${managerUrl}${namespace}`, {
            autoConnect: false,
            withCredentials: true,
            path,
            transports: ['polling', 'websocket'],
            timeout: 10000,
            reconnection: true,
            auth: {
                front: 'web',
            },
            query: {
                front: 'web',
            },
        });

        socket.on(REALTIME_SOCKET_EVENTS.tokenRefreshed, (token: string) => {
            void persistNewToken(token);
        });

        socket.on(REALTIME_SOCKET_EVENTS.message, handleRealtimeMessage);

        socket.on('connect', () => {
            setStatus('connected');
            setError(null);
        });

        socket.on('disconnect', (reason: string) => {
            setStatus('disconnected');

            if (!disconnectRequestedRef.current && reason === 'io server disconnect') {
                setStatus('connecting');
                socket.connect();
            }
        });

        socket.on('connect_error', (nextError: Error) => {
            setError(nextError);
            setStatus('error');

            if (nextError.message === 'UNAUTHORIZED') {
                dispatchUnauthorizedEventOnce();
            }
        });

        socketRef.current = socket;

        return socket;
    }, [handleRealtimeMessage]);

    const disconnect = useCallback(() => {
        disconnectRequestedRef.current = true;
        teardownSocket();
        setStatus('disconnected');
        setError(null);
    }, [teardownSocket]);

    const ensureConnected = useCallback(async (): Promise<Socket> => {
        if (typeof window === 'undefined') {
            throw new Error('Socket.IO client can only be used in the browser.');
        }

        if (!user?._id) {
            throw new Error('UNAUTHORIZED');
        }

        disconnectRequestedRef.current = false;
        const socket = initializeSocket();

        if (socket.connected) {
            return socket;
        }

        setStatus('connecting');
        setError(null);

        return new Promise<Socket>((resolve, reject) => {
            const cleanup = () => {
                socket.off('connect', handleConnect);
                socket.off('connect_error', handleError);
            };

            const handleConnect = () => {
                cleanup();
                resolve(socket);
            };

            const handleError = (nextError: Error) => {
                cleanup();
                reject(nextError);
            };

            socket.once('connect', handleConnect);
            socket.once('connect_error', handleError);
            socket.connect();
        });
    }, [initializeSocket, user?._id]);

    const subscribe = useCallback(<TPayload, >(subscription: RealtimeSubscription<TPayload>) => {
        const subscriptionId = `realtime-subscription-${++subscriptionIdRef.current}`;

        subscribersRef.current.set(subscriptionId, subscription as InternalRealtimeSubscription);

        return () => {
            subscribersRef.current.delete(subscriptionId);
        };
    }, []);

    useEffect(() => {
        if (!user?._id) {
            disconnect();
            return;
        }

        void ensureConnected().catch((nextError) => {
            const realtimeError = nextError instanceof Error ? nextError : new Error('REALTIME_CONNECTION_FAILED');
            setError(realtimeError);
            setStatus('error');

            if (realtimeError.message === 'UNAUTHORIZED') {
                dispatchUnauthorizedEventOnce();
            }
        });
    }, [disconnect, ensureConnected, user?._id]);

    useEffect(() => () => {
        teardownSocket();
    }, [teardownSocket]);

    const value = useMemo<RealtimeContextValue>(() => ({
        status,
        error,
        connected: status === 'connected',
        ensureConnected,
        disconnect,
        subscribe,
    }), [status, error, ensureConnected, disconnect, subscribe]);

    return (
        <RealtimeContext.Provider value={value}>
            {children}
        </RealtimeContext.Provider>
    );
}

export function useRealtime(): RealtimeContextValue {
    const context = useContext(RealtimeContext);

    if (!context) {
        throw new Error('useRealtime must be used within a RealtimeProvider.');
    }

    return context;
}

export function useRealtimeChannel<TPayload>({
    channel,
    topic,
    enabled = true,
    onMessage,
    filter,
}: UseRealtimeChannelOptions<TPayload>): void {
    const { subscribe } = useRealtime();
    const onMessageRef = useRef(onMessage);
    const filterRef = useRef(filter);

    useEffect(() => {
        onMessageRef.current = onMessage;
    }, [onMessage]);

    useEffect(() => {
        filterRef.current = filter;
    }, [filter]);

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }

        return subscribe<TPayload>({
            channel,
            topic,
            onMessage: (event) => {
                onMessageRef.current(event);
            },
            filter: (event) => filterRef.current ? filterRef.current(event) : true,
        });
    }, [channel, enabled, subscribe, topic]);
}
